import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Pre-launch splash for correlate.online.
 *
 * No header/footer chrome — full-bleed page. Italic 'C' monogram, manifesto,
 * email capture. Email goes to POST /api/waitlist (idempotent).
 *
 * Mounted at `/` when REACT_APP_LAUNCH_MODE === "splash"; available at
 * `/coming-soon` regardless so the founder can preview anytime.
 */
export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);

  // Tiny enter animation flag — staggered reveal via CSS classes below.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/waitlist", { email: trimmed, source: "splash" });
      setJoined(true);
      toast.success(data.already ? "You're already on the list." : "Welcome. The thread will hold.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add you. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="coming-soon-page"
      className="min-h-screen w-full sunrise-bg flex flex-col"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {/* Brand row */}
      <div className="px-6 sm:px-12 pt-10">
        <div
          className={`mono text-[11px] uppercase tracking-[0.32em] text-[hsl(var(--accent))] transition-opacity duration-1000 ${mounted ? "opacity-100" : "opacity-0"}`}
        >
          Cor<span className="text-muted-foreground/60">·</span>relate · Vol. I
        </div>
      </div>

      {/* HERO */}
      <main className="flex-1 flex flex-col justify-center max-w-5xl mx-auto w-full px-6 sm:px-12 py-16 sm:py-24">
        <div className="flex flex-col lg:flex-row lg:items-end gap-12 lg:gap-20">
          {/* Italic 'C' monogram */}
          <div
            data-testid="coming-soon-monogram"
            className={`serif italic text-[180px] sm:text-[260px] lg:text-[320px] leading-[0.78] text-[hsl(var(--primary))] select-none transition-all duration-[1400ms] ease-out ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
            style={{ fontWeight: 500 }}
          >
            C
          </div>

          <div className="flex-1">
            <h1
              className={`serif text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-foreground transition-all duration-1000 delay-100 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
            >
              The same truth,
              <br />
              <em className="text-[hsl(var(--accent))] font-light not-italic">in every tongue.</em>
            </h1>

            <p
              className={`mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-foreground/75 transition-all duration-1000 delay-200 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
            >
              An archive that places teachings from religion, philosophy, esoteric tradition, hidden history, and declassified record side by side — so we may meet what's unfolding with the only instruments that work:&nbsp;
              <span className="text-[hsl(var(--primary))]">empathy</span>,&nbsp;
              <span className="text-[hsl(var(--primary))]">love</span>, and&nbsp;
              <span className="text-[hsl(var(--primary))]">truth</span>.
            </p>

            <p
              className={`mt-6 max-w-xl serif italic text-lg sm:text-xl text-[hsl(var(--accent))]/90 transition-all duration-1000 delay-300 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
            >
              Opening soon. Be curious. Be unafraid.
            </p>

            {/* Email capture */}
            <div
              className={`mt-12 transition-all duration-1000 delay-500 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
            >
              {!joined ? (
                <form onSubmit={submit} data-testid="waitlist-form" className="flex flex-col sm:flex-row gap-3 max-w-md">
                  <input
                    data-testid="waitlist-email-input"
                    type="email"
                    required
                    autoComplete="email"
                    inputMode="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email"
                    className="input-line flex-1 text-sm bg-card/60"
                  />
                  <button
                    data-testid="waitlist-submit"
                    type="submit"
                    disabled={submitting}
                    className="btn-gold disabled:opacity-50"
                  >
                    {submitting ? "Sending…" : "Notify me"}
                  </button>
                </form>
              ) : (
                <div
                  data-testid="waitlist-success"
                  className="paper p-5 max-w-md flex items-start gap-4"
                >
                  <span className="serif italic text-3xl text-[hsl(var(--primary))] leading-none">✓</span>
                  <div>
                    <p className="serif text-lg text-foreground leading-snug">
                      You're on the list.
                    </p>
                    <p className="mt-1 mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                      We'll write the moment the archive opens.
                    </p>
                  </div>
                </div>
              )}
              <p className="mt-4 mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground/75">
                No spam, ever. One email at launch. Unsubscribe in one click.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer / Manifesto excerpt */}
      <footer className="hairline-t mt-8">
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-8 sm:py-10 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <p className="serif italic text-sm sm:text-base text-muted-foreground max-w-2xl leading-relaxed">
            "The Logos is the Tao. The Tao is the Aum. We do not invent the correlations — we restore them."
          </p>
          <span className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground/60">
            correlate.online
          </span>
        </div>
      </footer>
    </div>
  );
}
